import React, { useState, useEffect } from 'react';
import { X, Save, Trash2 } from 'lucide-react';
import { Categorie, createCategorie, updateCategorie, deleteCategorie } from '../lib/services/CategoryService';
import { DEFAULT_COLORS, CATEGORY_COLORS } from '../config/constants';
import { CATEGORY_ICONS, CategoryIconKey, suggestCategoryIcon } from '../lib/categoryIcons';
import CategoryIconBadge from './CategoryIconBadge';

interface CategoryFormProps {
  category?: Categorie | undefined;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (category: Categorie) => Promise<{ success: boolean; error?: string }>;
  title?: string;
  defaultType?: 'depense' | 'revenu';
}

const CategoryForm: React.FC<CategoryFormProps> = ({
  category,
  isOpen,
  onClose,
  onSubmit,
  title = 'Nouvelle catégorie',
  defaultType = 'depense'
}) => {
  const [formData, setFormData] = useState({
    nom: '',
    type: defaultType as 'depense' | 'revenu',
    couleur: DEFAULT_COLORS.CATEGORY,
    icone: 'tag' as CategoryIconKey,
    commentaire: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const predefinedColors = CATEGORY_COLORS;

  useEffect(() => {
    if (category) {
      setFormData({
        nom: category.nom || '',
        type: category.type || defaultType,
        couleur: category.couleur || '#00E676',
        icone: (category.icone as CategoryIconKey) || suggestCategoryIcon(category.nom || ''),
        commentaire: category.commentaire || '',
      });
    } else {
      setFormData({
        nom: '',
        type: defaultType,
        couleur: DEFAULT_COLORS.CATEGORY,
        icone: 'tag',
        commentaire: '',
      });
    }
    setError(null);
  }, [category, isOpen, defaultType]);

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'nom' && !category?.id) {
        next.icone = suggestCategoryIcon(value);
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.nom.trim()) {
      setError('Le nom de la catégorie est obligatoire');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const categoryData: Categorie = {
        nom: formData.nom.trim(),
        type: formData.type,
        couleur: formData.couleur,
        icone: formData.icone,
        ...(formData.commentaire.trim() && { commentaire: formData.commentaire.trim() }),
      };

      let result;
      if (category && category.id) {
        // Mise à jour d'une catégorie existante
        const updateResult = await updateCategorie(category.id, categoryData);
        result = {
          success: !updateResult.error,
          error: updateResult.error?.message || updateResult.error
        };
      } else {
        // Création d'une nouvelle catégorie
        const createResult = await createCategorie(categoryData);
        result = {
          success: !createResult.error,
          error: createResult.error?.message || createResult.error
        };
      }

      if (result.success) {
        await onSubmit(categoryData);
      } else {
        setError(result.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inattendue');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!category?.id) {
      onClose();
      return;
    }

    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ? Cette action est irréversible.')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await deleteCategorie(category.id);
      
      if (result.success) {
        await onSubmit(category); // Pour déclencher le rechargement
      } else {
        setError(result.error?.message || 'Erreur lors de la suppression');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inattendue');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-dark-800 border border-dark-700 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
        {/* En-tête */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">{title}</h2>
          <button
            onClick={onClose}
            className="text-dark-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Nom de la catégorie */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Nom de la catégorie *
            </label>
            <input
              type="text"
              value={formData.nom}
              onChange={(e) => handleInputChange('nom', e.target.value)}
              className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary-500"
              placeholder="Ex: Alimentation"
              required
            />
          </div>

          {/* Type de catégorie */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Type
            </label>
            <div className="flex bg-dark-800 rounded-lg p-1">
              <button
                type="button"
                onClick={() => handleInputChange('type', 'depense')}
                className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                  formData.type === 'depense' 
                    ? 'bg-primary-500 text-white' 
                    : 'text-dark-400 hover:text-white'
                }`}
              >
                Dépense
              </button>
              <button
                type="button"
                onClick={() => handleInputChange('type', 'revenu')}
                className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                  formData.type === 'revenu' 
                    ? 'bg-primary-500 text-white' 
                    : 'text-dark-400 hover:text-white'
                }`}
              >
                Revenu
              </button>
            </div>
          </div>

          {/* Commentaire */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Commentaire (optionnel)
            </label>
            <textarea
              value={formData.commentaire}
              onChange={(e) => handleInputChange('commentaire', e.target.value)}
              className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary-500 resize-none"
              placeholder="Informations supplémentaires sur cette catégorie..."
              rows={3}
            />
          </div>

          {/* Sélecteur de couleur */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Couleur
            </label>
            <div className="flex items-center space-x-3 mb-3">
              <input
                type="color"
                value={formData.couleur}
                onChange={(e) => handleInputChange('couleur', e.target.value)}
                className="w-12 h-10 bg-transparent border border-dark-700 rounded-lg cursor-pointer"
              />
              <span className="text-dark-400 text-sm">Couleur personnalisée</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {predefinedColors.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => handleInputChange('couleur', color)}
                  className={`w-8 h-8 rounded-full border-2 transition-colors ${
                    formData.couleur === color 
                      ? 'border-white' 
                      : 'border-dark-700 hover:border-white'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          {/* Sélecteur d'icône */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Icône
            </label>
            <div className="flex items-center gap-3 mb-3">
              <CategoryIconBadge color={formData.couleur} icon={formData.icone} size={48} />
              <span className="text-dark-400 text-sm">Aperçu</span>
            </div>
            <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto pr-1">
              {CATEGORY_ICONS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  title={item.label}
                  onClick={() => handleInputChange('icone', item.key)}
                  className={`rounded-xl p-0.5 border-2 transition-colors ${
                    formData.icone === item.key
                      ? 'border-primary-500'
                      : 'border-transparent hover:border-dark-500'
                  }`}
                >
                  <CategoryIconBadge
                    color={formData.icone === item.key ? formData.couleur : '#3a3f4b'}
                    icon={item.key}
                    size={36}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Gestion des erreurs */}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between space-x-3 pt-4">
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-dark-400 hover:text-white transition-colors"
              >
                Annuler
              </button>
              {category?.id && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={loading}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Supprimer</span>
                </button>
              )}
            </div>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-lg transition-colors"
            >
              <Save className="w-4 h-4" />
              <span>{loading ? 'Sauvegarde...' : 'Sauvegarder'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryForm; 